import {Inject, Injectable} from '@angular/core';

import {ENV_CONFIG} from '@environments/environment-config/environment.interface';
import type { Environment } from '@environments/environment-config/environment.interface';


@Injectable({
  providedIn: 'root'
})
export class GeneralUrlsService {

  private readonly apiAuthority: string;
  private urls: string [] = [];
  public publicEndpoints: Record<string, string>;
  public privateEndpoints: Record<string, string>;

  constructor(@Inject(ENV_CONFIG) private config: Environment) {
    this.apiAuthority = config.environment.apiAuthority;
    this.publicEndpoints = this.withAuthority(config.environment.publicEndpoints);
    this.privateEndpoints = this.withAuthority(config.environment.privateEndpoints);

    this.notIntercepted();

  }

  public getPublicEndpoint(name: keyof typeof this.config.environment.publicEndpoints): string {
    return this.apiAuthority + this.config.environment.publicEndpoints[name];
  }

  public getPrivateEndpoint(name: keyof typeof this.config.environment.privateEndpoints): string {
    return this.apiAuthority + this.config.environment.privateEndpoints[name];
  }

  private withAuthority(endpoints: Record<string, string>): Record<string, string> {
    const prefixed: Record<string, string> = {};
    for (const key in endpoints) {
      prefixed[key] = this.apiAuthority + endpoints[key];
    }
    return prefixed;
  }

  get skipUrls(): string [] {
    return this.urls;
  }

  set _urls(urls: string []) {
    this.urls.push(...urls);
  }

  public notIntercepted(): void {
    /**
     * This method handles all url that will not need a notification success for users.
     */
    const urls = [
      this.publicEndpoints['login'],
      this.privateEndpoints['logout'],
      this.publicEndpoints['validateSetupToken'],
    ];
    this._urls = this.getPath(urls);
  }

  public addDynamicUrl(url: string): void {
    /**
     * This method handles the build urls with params
     */
    this._urls = this.getPath([url]);
  }

  public getPath(urls: string[]): string[] {
    /**
     * return the uri part of a complete url.
     */
    return urls.map((fullUrl) => fullUrl.split(this.apiAuthority)[1]);
  }

  public buildUrlPathParameters(url: string, parameters: {[parameterName: string]: string}): string {
    /**
     * This function is for building an url that contain a path parameters.
     */
    const placeholders = url.match(/({[a-z_A-Z]*})/g);
    if (placeholders) {
      placeholders.forEach((placeholder: string) => {
        const key = placeholder.slice(1, -1);
        const value = parameters[key];
        if (!value) {
          throw new Error(`Parameter ${key} was not provided`);
        }

        url = url.replace(placeholder, encodeURIComponent(value));
      });
    }
    return url;
  }
}
